import { useEffect, useMemo, useState } from "react";
import { api, formatINR, downloadReport } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { Plus, Search, FileSpreadsheet, FileText, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { inputCls, Field } from "./Projects";
import { useBranch } from "@/contexts/BranchContext";
import { BranchSelect } from "@/components/BranchSelect";
import { BranchFilter } from "@/components/BranchFilter";
import { BulkUploadButtons, BulkUploadResultBanner } from "@/components/BulkUploadControls";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ActiveStatusCell, ActiveStatusFilter, matchActiveFilter } from "@/components/ActiveStatusCell";

/** Generic Master List page used for Vendors, Clients, Items */
export default function MasterList({ resource, title, columns, formFields, idField, exportName, defaultRecord, bulkUploadMaster, bulkUploadFilename, activeField, bulkAssignBranch, bulkUpdate }) {
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterActive, setFilterActive] = useState("active");
  const { active: activeBranch } = useBranch();

  // Sprint 2026-Q1 — promote `search` to a server-side query parameter
  // (`q`) so masters with many rows can find values that are pushed off
  // page 1 by the `created_at DESC` sort (e.g. after a fresh bulk
  // upload that adds N new rows on top, demoting older entries). The
  // backend `make_crud` regex-matches `q` against a per-collection
  // text-field list and returns the filtered + paginated subset.
  // Debounce so we don't fire a request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = {};
    if (filterBranch) p.branch_id = filterBranch;
    if (debouncedSearch) p.q = debouncedSearch;
    return p;
  }, [filterBranch, debouncedSearch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList(`/${resource}`, params);
  const load = reload;
  const openFormTab = useFormTab();

  const openNew = () => openFormTab({
    path: `/${resource}/new`,
    label: `New ${title.replace(/s$/, "")}`,
    icon: Plus,
    parent: `/${resource}`,
    onSaved: load,
    render: ({ close }) => (
      <MasterModal resource={resource} title={title} formFields={formFields} defaultRecord={defaultRecord} activeField={activeField} record={null} activeBranch={activeBranch} onClose={close} onSaved={close} />
    ),
  });
  const openEdit = (row) => openFormTab({
    path: `/${resource}/edit/${row.id}`,
    label: `Edit · ${row.code || row.name || row.member_code || title.slice(0, -1)}`,
    parent: `/${resource}`,
    onSaved: load,
    render: ({ close }) => (
      <MasterModal resource={resource} title={title} formFields={formFields} defaultRecord={defaultRecord} activeField={activeField} record={row} activeBranch={activeBranch} onClose={close} onSaved={close} />
    ),
  });

  // Server returns rows already matching the `q` search via
  // /api/{resource}?q=... — we only need client-side filters for
  // branch (UI dropdown) + active/inactive status (UI pill).
  const filtered = list.filter(x =>
    (!filterBranch || x.branch_id === filterBranch) &&
    (!activeField || matchActiveFilter(x, filterActive, activeField))
  );

  // Sprint 2026-Q1 — Members-only Bulk Assign Branch dialog (driven by
  // the `bulkAssignBranch` prop). Lets an admin pick a target branch
  // and apply it to every member whose `branch_id` is empty/missing in
  // one POST. Legacy members imported before Branch was made mandatory
  // are typically the ones that need this.
  const [bulkBranchOpen, setBulkBranchOpen] = useState(false);
  const [bulkBranchCount, setBulkBranchCount] = useState(0);
  const [bulkUpdResult, setBulkUpdResult] = useState(null);
  const [bulkUpdBusy, setBulkUpdBusy] = useState(false);

  async function downloadUpdateTemplate() {
    try {
      const res = await api.get(bulkUpdate.templateEndpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = bulkUpdate.filename || `${resource}_bulk_update_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Template download failed"); }
  }

  async function handleBulkUpdateFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBulkUpdBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(bulkUpdate.uploadEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkUpdResult(data);
      toast.success(`Updated ${data.updated} of ${data.total} rows`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally { setBulkUpdBusy(false); }
  }

  useEffect(() => {
    if (!bulkAssignBranch) return;
    api.get(`/${bulkAssignBranch.countEndpoint}`)
      .then(r => setBulkBranchCount(Number(r.data?.count || 0)))
      .catch(() => setBulkBranchCount(0));
  }, [bulkAssignBranch, list]);

  async function handleDelete(id) {
    if (!window.confirm(`Delete this ${title.slice(0, -1).toLowerCase()}?`)) return;
    await api.delete(`/${resource}/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader onRefresh={load} refreshing={loading}
        eyebrow="Master Data"
        title={title}
        actions={
          <>
            {bulkUploadMaster && (
              <BulkUploadButtons master={bulkUploadMaster} filename={bulkUploadFilename || `${bulkUploadMaster}_master_template.xlsx`} onUploaded={load} />
            )}
            {bulkUpdate && (
              <>
                <button data-testid={`${resource}-bulk-update-template-btn`} onClick={downloadUpdateTemplate}
                  title="Download an Excel sheet pre-filled with current data — edit and upload it back"
                  className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                  <FileSpreadsheet size={14} /> Update Template
                </button>
                <label data-testid={`${resource}-bulk-update-btn`}
                  className={`bg-white border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 cursor-pointer ${bulkUpdBusy ? "opacity-50 pointer-events-none" : ""}`}>
                  <FileSpreadsheet size={14} /> {bulkUpdBusy ? "Updating…" : "Bulk Update"}
                  <input type="file" accept=".xlsx" className="hidden" onChange={handleBulkUpdateFile}
                    data-testid={`${resource}-bulk-update-input`} />
                </label>
              </>
            )}
            {bulkAssignBranch && bulkBranchCount > 0 && (
              <button
                data-testid={`${resource}-bulk-branch-btn`}
                onClick={() => setBulkBranchOpen(true)}
                title={`${bulkBranchCount} ${title.toLowerCase()} have no branch assigned`}
                className="bg-white border border-amber-400 hover:bg-amber-50 text-amber-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
              >
                Set Branch ({bulkBranchCount})
              </button>
            )}
            <button data-testid={`export-${resource}-xlsx`} onClick={() => downloadReport(exportName, "xlsx")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button data-testid={`export-${resource}-pdf`} onClick={() => downloadReport(exportName, "pdf")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileText size={14} /> PDF
            </button>
            <button data-testid={`add-${resource}-btn`} onClick={openNew} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New
            </button>
          </>
        }
      />

      {bulkUploadMaster && <BulkUploadResultBanner master={bulkUploadMaster} />}

      {bulkUpdate && bulkUpdResult && (
        <div className="mb-4 border border-slate-200 bg-white px-4 py-3" data-testid={`${resource}-bulk-update-result`}>
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-slate-700">
              Bulk Update — <span className="text-emerald-700">{bulkUpdResult.updated} updated</span>
              {bulkUpdResult.skipped > 0 && <> · <span className="text-amber-700">{bulkUpdResult.skipped} skipped</span></>}
              {" "}of {bulkUpdResult.total} rows
            </div>
            <button className="text-slate-400 hover:text-slate-600" onClick={() => setBulkUpdResult(null)}
              data-testid={`${resource}-bulk-update-result-close`}><X size={14} /></button>
          </div>
          {(bulkUpdResult.errors || []).length > 0 && (
            <ul className="mt-2 max-h-40 overflow-y-auto text-[11px] text-red-700 list-disc pl-5 space-y-0.5">
              {bulkUpdResult.errors.map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 max-w-3xl">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            className="w-full border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316]" />
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid={`${resource}-filter-branch`} />
        {activeField && (
          <ActiveStatusFilter value={filterActive} onChange={setFilterActive} testid={`${resource}-filter-active`} />
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={`No ${title.toLowerCase()} yet`} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid={`${resource}-table`}>
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  {columns.map(c =>
                    <th key={c.key} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-5">{c.label}</th>
                  )}
                  {activeField && (
                    <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-5">Status</th>
                  )}
                  <th className="py-3 px-5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    {columns.map(c => (
                      <td key={c.key} className="py-3 px-5 text-sm text-slate-700">
                        {c.format === "money" ? formatINR(row[c.key]) : (row[c.key] || "—")}
                      </td>
                    ))}
                    {activeField && (
                      <td className="py-3 px-5">
                        <ActiveStatusCell row={row} resource={resource} field={activeField} onChanged={load} />
                      </td>
                    )}
                    <td className="py-3 px-5 text-xs whitespace-nowrap">
                      <button onClick={() => openEdit(row)} className="text-[#F97316] hover:underline mr-3">Edit</button>
                      <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}

      {bulkAssignBranch && bulkBranchOpen && (
        <BulkSetBranchDialog
          title={title}
          count={bulkBranchCount}
          endpoint={bulkAssignBranch.applyEndpoint}
          onClose={() => setBulkBranchOpen(false)}
          onApplied={() => { setBulkBranchOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/** Modal: pick a target Branch and assign it to every <Master> currently
 * lacking a `branch_id`. Used today by the Members page but designed to
 * be reusable for any branch-scoped master that suffers the same
 * legacy-import drift (e.g. Items, Warehouses). */
function BulkSetBranchDialog({ title, count, endpoint, onClose, onApplied }) {
  const [branchId, setBranchId] = useState("");
  const [branchName, setBranchName] = useState("");
  const [busy, setBusy] = useState(false);

  async function apply() {
    if (!branchId) { toast.error("Please pick a target branch"); return; }
    if (!window.confirm(`Assign ${count} branchless ${title.toLowerCase()} to "${branchName}"?\n\nThis cannot be undone via this dialog — each row would need to be edited individually to change branches again.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${endpoint}`, { branch_id: branchId });
      toast.success(`${data.updated || 0} ${title.toLowerCase()} updated`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4" data-testid="bulk-set-branch-dialog">
      <div className="bg-white w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">Assign Branch in Bulk</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          <span className="font-bold text-amber-700">{count}</span> {title.toLowerCase()} currently
          have no branch assigned. Pick a target branch below to assign them all in one step.
        </p>
        <Field label="Target Branch *">
          <BranchSelect
            value={branchId}
            onChange={(id, name) => { setBranchId(id); setBranchName(name); }}
            testid="bulk-branch-target"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 mt-4">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button
            data-testid="bulk-set-branch-apply"
            disabled={busy || !branchId}
            onClick={apply}
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {busy ? "Applying…" : `Apply to ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MasterModal({ resource, title, formFields, record, defaultRecord, activeField, activeBranch, onClose, onSaved }) {
  const [form, setForm] = useState(record || {
    ...defaultRecord,
    branch_id: defaultRecord?.branch_id ?? (activeBranch?.id || ""),
    branch_name: defaultRecord?.branch_name ?? (activeBranch?.branch_name || ""),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  async function save(e) {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    setSaving(true);
    try {
      // coerce numeric fields
      const payload = { ...form };
      formFields.forEach(f => { if (f.type === "number") payload[f.key] = Number(payload[f.key] || 0); });
      if (record) await api.put(`/${resource}/${record.id}`, payload);
      else await api.post(`/${resource}`, payload);
      toast.success(record ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="master-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <h2 className="font-display text-xl font-bold">{record ? "Edit" : "New"} {title.slice(0, -1)}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[1400px] mx-auto">
          <Field label="Branch *">
            <BranchSelect value={form.branch_id} onChange={(id, name) => setForm({ ...form, branch_id: id, branch_name: name })} testid={`${resource}-branch-select`} />
          </Field>
          {formFields.map(f => (
            <Field key={f.key} label={f.label + (f.required ? " *" : "")} full={f.full}>
              {f.type === "select" ? (
                <select value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} className={inputCls}>
                  {f.options.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} rows={2} className={inputCls} />
              ) : (
                <input
                  data-testid={`field-${f.key}`}
                  type={f.type || "text"}
                  step={f.type === "number" ? "0.01" : undefined}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={e => set(f.key, e.target.value)}
                  className={inputCls}
                />
              )}
            </Field>
          ))}
          {activeField && (
            <Field label="Status">
              <label className="flex items-center gap-2 text-sm pt-2" data-testid={`${resource}-active-toggle`}>
                <input type="checkbox" checked={form[activeField] !== false}
                       onChange={e => set(activeField, e.target.checked)}
                       className="w-4 h-4 accent-[#F97316]" />
                <span className={form[activeField] !== false ? "text-emerald-700 font-bold" : "text-slate-500"}>
                  {form[activeField] !== false ? "Active" : "Inactive (hidden from dropdowns)"}
                </span>
              </label>
            </Field>
          )}
          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-master-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
    </div>
  );
}
